const _excluded = ["id", "text"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import { useEffect, useMemo, useState } from 'react';
import { Button, Callout, useOverlayContext, useStateInRef } from 'seagull';
import { useStore, useStoreActions } from "../../contexts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { Intent } from "../../infra/access-control";
import { handlePromise } from "../../infra/utils";
import { getAccountingDetails } from "../../integrations/Accounting/accounting.utils";
import { accountingIconMap } from "../../integrations/Accounting/AccountingIcon/accountingIconMap";
import { useOpenMapContactsModal } from "../../integrations/Accounting/MapContactsModal/useOpenMapContactsModal";
import { useOpenMapServicesModal } from "../../integrations/Accounting/MapServicesModal/useOpenMapServicesModal";
import { invoiceModel } from "../../models";
import { selectors } from "../../store/state-selectors";
import { getAccountingSectionCallout, getServiceTemplateIdsV1 } from "./FullPageInvoice.utils";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
const syncLoadingText = 'Syncing...';
var SyncingState = /*#__PURE__*/function (SyncingState) {
  SyncingState["Syncing"] = "syncing";
  SyncingState["SyncingRetry"] = "syncingRetry";
  SyncingState["SyncingRetryWithoutTimeActivities"] = "syncingRetryWithoutTimeActivities";
  return SyncingState;
}(SyncingState || {});
export default function FullPageInvoiceAccountingCallout(props) {
  const {
    invoice,
    contacts,
    serviceTemplates,
    relationship,
    reloadInvoice,
    accounting,
    integration
  } = props;
  const {
    showErrorToast,
    showSuccessToast
  } = useOverlayContext();
  const {
    api
  } = useStoreActions();
  const {
    SERVICE_TEMPLATE_BUNDLE
  } = useFeatureFlags();
  const invoiceClientContactId = useMemo(() => {
    const invoiceContact = contacts.find(c => c.id() === relationship.clientContactId);
    return invoiceContact ? invoiceContact.id() : null;
  }, [relationship.clientContactId, contacts]);
  const serviceTemplateIds = SERVICE_TEMPLATE_BUNDLE ? getAccountingDetails(accounting).policy.getServiceTemplateIds(invoice) : getServiceTemplateIdsV1(invoice);
  const openMapServicesModal = useOpenMapServicesModal(serviceTemplateIds, reloadInvoice);
  const openMapContactsModal = useOpenMapContactsModal(invoiceClientContactId, reloadInvoice);
  const [isSyncing, setIsSyncing] = useState(null);
  const [isSyncingPayment, setIsSyncingPayment] = useState(false);
  const [isSyncingRefund, setIsSyncingRefund] = useState(false);
  const isLinkedToAccounting = useStore(selectors.integration.accounting(accounting).isLinked);
  const featureFlags = useFeatureFlags();
  const getState = useStateInRef({
    isSyncing,
    isSyncingPayment,
    isSyncingRefund,
    onChange: props.onChange,
    showSyncSuccessToast: formattedNumber => showSuccessToast("Invoice ".concat(formattedNumber, " was successfully synced"), 'sync-success-toast'),
    showSyncErrorToast: formattedNumber => showErrorToast("Failed to sync invoice ".concat(formattedNumber), 'sync-failure-toast'),
    showSyncPaymentSuccessToast: () => showSuccessToast('Payment was successfully synced', 'payment-sync-success'),
    showSyncPaymentFailureToast: () => showErrorToast('Failed to sync payment', 'payment-sync-failure'),
    showSyncRefundSuccessToast: () => showSuccessToast('Refund was successfully synced', 'refund-sync-success'),
    showSyncRefundFailureToast: () => showErrorToast('Failed to sync refund', 'refund-sync-failure')
  });
  useEffect(() => {
    if (!integration) return;
    const {
      isSyncing,
      isSyncingPayment,
      isSyncingRefund,
      onChange,
      showSyncSuccessToast,
      showSyncErrorToast,
      showSyncPaymentFailureToast,
      showSyncPaymentSuccessToast,
      showSyncRefundSuccessToast,
      showSyncRefundFailureToast
    } = getState();
    if (isSyncing) {
      setIsSyncing(null);
      onChange(integration);
      if (integration.isInvoiceSyncFailed()) {
        showSyncErrorToast(invoice.formattedNumber);
      } else if (integration.isInvoiceRecorded() || integration.isPaymentRecorded()) {
        showSyncSuccessToast(invoice.formattedNumber);
      }
    }
    if (isSyncingPayment) {
      setIsSyncingPayment(false);
      onChange(integration);
      if (integration.isPaymentSyncFailed()) {
        showSyncPaymentFailureToast();
      } else if (integration.isPaymentRecorded()) {
        showSyncPaymentSuccessToast();
      }
    }
    if (isSyncingRefund) {
      setIsSyncingRefund(false);
      onChange(integration);
      if (integration.isRefundSyncFailed()) {
        showSyncRefundFailureToast();
      } else if (integration.isRefundRecorded()) {
        showSyncRefundSuccessToast();
      }
    }
  }, [integration, invoice]);
  const callout = isLinkedToAccounting ? getAccountingSectionCallout({
    accounting,
    items: invoice.items,
    relationship,
    contacts,
    serviceTemplates,
    integration: integration,
    invoiceStatus: invoiceModel.statusType(invoice),
    numRefunds: invoiceModel.numRefunds(invoice),
    isZeroAmount: invoice.isTotalAmountZero,
    syncAction: {
      id: 'sync-invoice',
      text: 'Sync',
      loading: isSyncing === SyncingState.Syncing,
      loadingText: syncLoadingText,
      onClick: () => syncInvoice({
        syncState: SyncingState.Syncing
      })
    },
    retrySyncAction: {
      id: 'retry-sync-invoice',
      text: 'Retry sync',
      loading: isSyncing === SyncingState.SyncingRetry,
      disabled: !!isSyncing && isSyncing !== SyncingState.SyncingRetry,
      loadingText: syncLoadingText,
      onClick: () => syncInvoice({
        isRetry: true,
        syncState: SyncingState.SyncingRetry
      })
    },
    retrySyncWithoutTimeActivitiesAction: {
      id: 'retry-sync-without-time-activities',
      text: 'Sync without linked time activities',
      loading: isSyncing === SyncingState.SyncingRetryWithoutTimeActivities,
      disabled: !!isSyncing && isSyncing !== SyncingState.SyncingRetryWithoutTimeActivities,
      loadingText: syncLoadingText,
      onClick: () => syncInvoice({
        isRetry: true,
        includeTimeActivities: false,
        syncState: SyncingState.SyncingRetryWithoutTimeActivities
      })
    },
    mapContactsAction: {
      id: 'map-contact',
      text: 'Map contact',
      onClick: () => openMapContactsModal(accounting)
    },
    mapServicesAction: {
      id: 'map-services',
      text: 'Map services',
      intent: Intent.AccountingMapServices,
      onClick: () => openMapServicesModal(accounting)
    },
    syncPaymentAction: {
      id: 'sync-payment',
      text: 'Sync',
      loading: isSyncingPayment,
      loadingText: syncLoadingText,
      onClick: () => triggerSync({
        sendRequest: () => api.accounting.syncPayment({
          accounting,
          invoiceId: invoice.id
        }),
        onLoading: () => setIsSyncingPayment(true),
        onError: () => {
          getState().showSyncPaymentFailureToast();
          setIsSyncingPayment(false);
        }
      })
    },
    syncRefundAction: {
      id: 'sync-refund',
      text: 'Sync',
      loading: isSyncingRefund,
      loadingText: syncLoadingText,
      onClick: () => triggerSync({
        sendRequest: () => api.accounting.syncRefund(accounting, invoice.id),
        onLoading: () => setIsSyncingRefund(true),
        onError: () => {
          getState().showSyncRefundFailureToast();
          setIsSyncingRefund(false);
        }
      })
    },
    featureFlags
  }) : null;
  async function syncInvoice(_ref) {
    let {
      isRetry,
      syncState,
      includeTimeActivities
    } = _ref;
    triggerSync({
      sendRequest: () => {
        return isRetry ? api.accounting.retrySyncInvoice(accounting, invoice.id, includeTimeActivities) : api.accounting.syncInvoice(accounting, invoice.id);
      },
      onLoading: () => setIsSyncing(syncState),
      onError: () => {
        getState().showSyncErrorToast(invoice.formattedNumber);
        setIsSyncing(null);
      }
    });
  }
  async function triggerSync(_ref2) {
    let {
      sendRequest,
      onLoading,
      onError
    } = _ref2;
    onLoading();
    const {
      success
    } = await handlePromise(sendRequest());
    if (!success) {
      onError();
    }
  }
  return callout ? /*#__PURE__*/_jsx(Callout, {
    intent: callout.intent,
    icon: accountingIconMap[accounting][callout.status],
    "data-testid": "fpi-".concat(callout.id),
    withAction: !!callout.actions.length && /*#__PURE__*/_jsx(_Fragment, {
      children: callout.actions.map(_ref3 => {
        let {
            id,
            text
          } = _ref3,
          rest = _objectWithoutProperties(_ref3, _excluded);
        return /*#__PURE__*/_jsx(Button, _objectSpread(_objectSpread({
          "data-testid": id,
          skin: "primaryOutline"
        }, rest), {}, {
          children: text
        }), id);
      })
    }),
    children: callout.text
  }) : null;
}